---
title: Add tools to a persona
summary: Bind a capability to an adapter so a persona can take real actions — and wire credentials where required.
visibility: authenticated
feature: tool_framework
audience: author-admin
section: Advanced authoring
order: 97
last_verified: 2026-07-21
owner: docs
---

# Add tools to a persona

A **tool** lets a persona take a real action — accept a payment, look up a price, send a
message. Under the hood, a **capability** (the vendor-neutral action, like "create a payment
link") is bound to an **adapter** (the implementation that performs it). You bind tools on
the **Tools** card in the persona's [Workshop](/help/advanced/use-the-workshop) (Advanced mode).

## Bind a tool

1. On the **Tools** card, click **+ Add tool**.
2. **Choose a capability** — the action you want to enable (shown by its id and category).
3. **Choose an adapter** — one of the implementations that provides that capability. Each
   shows its name and kind (**Native** or **MCP**).

The binding appears on the card. You can change the adapter later from the inline dropdown,
or remove the binding with the **✕**.

## Credentials for a tool

Some capabilities need credentials (for example, payments or delivery). The Workshop shows a
binding's credential set as **read-only** — wiring a credential set to a binding is done on
the admin tools surface, not here. If a capability that **requires credentials** has none
wired, **Save will be rejected** until it's connected. Ask an administrator to attach the
right [credential set](/help/how-to/manage-credentials) to the binding.

## Browse and connect adapters (admin)

Administrators can open **Tools** under Admin (`/admin/tools`) to browse all **Capabilities**
and **Adapters**, **Connect** an MCP adapter, **Test** an adapter, and see which personas
use it. Some capabilities are gated behind an **approval** queue or are only
callable from inside a workflow step.

## Save

Binding or unbinding a tool stages a draft change — click **Save** in the top bar to commit.
Watch the Review tab's wiring subsection for unbound adapters or capabilities used by a
skill/workflow that isn't bound yet.

## See also

- [Add skills](/help/advanced/add-skills) — skills declare the capabilities they need.
- [Manage credentials](/help/how-to/manage-credentials) — where credential sets come from.
